import { applyDamage } from './body';
import { Boss } from './boss';
import type { Bullet } from './bullet';
import type { Enemy } from './enemy';
import type { Box } from './geometry';
import type { Pickup } from './pickup';
import { HitOutcome, PlayerState } from './player';
import type { Player } from './player';
import { PlayerTuning } from './tuning';
import type { World } from './world';

/**
 * What the collision pass asks of the run (class diagram, `HitRules`): declared here and realized
 * by `Run`, so the resolver never imports the run and never sees the bus.
 */
export interface HitRules {
  enemyHit(enemy: Enemy): void;
  destroyEnemy(enemy: Enemy): void;
  playerDied(): void;
  /** The ship caught the pickup, already marked spent: `Run` applies it and publishes it. */
  pickupCollected(pickup: Pickup): void;
  /** The shield charge absorbed a hit (GDD v0.8 §4.4). */
  shieldBroken(): void;
  /** A shot or the bomb hit the boss, which is fighting: `Run` applies, publishes and phases. */
  damageBoss(amount: number): void;
  /** An enemy bullet grazed the ship, already marked (GDD v0.31 §4.1): `Run` publishes it. */
  bulletGrazed(bullet: Bullet): void;
}

function overlaps(a: Box, b: Box): boolean {
  return (
    Math.abs(a.position.x - b.position.x) < (a.width + b.width) / 2 &&
    Math.abs(a.position.y - b.position.y) < (a.height + b.height) / 2
  );
}

// The ship's 6 × 6 hitbox (GDD v0.24 §4.1): only it counts, never the sprite — except while a shield
// charge is held, when the drawn 36 × 36 ring is the shape, so any touch consumes it (GDD v0.19 §4.4).
const HITBOX = PlayerTuning.hitboxSize;
const SHIELD = PlayerTuning.shieldSize;

// A width × height box centred on the ship against another box: the hitbox, the ring, the graze
// box and the collection box all use it.
function inShipBox(player: Player, box: Box, width: number, height: number): boolean {
  return (
    Math.abs(player.position.x - box.position.x) < (width + box.width) / 2 &&
    Math.abs(player.position.y - box.position.y) < (height + box.height) / 2
  );
}

function touchesPlayer(player: Player, box: Box): boolean {
  const size = player.shield ? SHIELD : HITBOX;
  return inShipBox(player, box, size, size);
}

// The drawn ship (GDD v0.31 §4.1): a bullet in it but off the hitbox grazes, once, and only a
// vulnerable ship without a shield charge grazes.
const GRAZE_WIDTH = PlayerTuning.grazeWidth;
const GRAZE_HEIGHT = PlayerTuning.grazeHeight;

function grazes(player: Player, bullet: Bullet): boolean {
  return (
    !bullet.grazed &&
    player.state === PlayerState.Vulnerable &&
    !player.shield &&
    inShipBox(player, bullet, GRAZE_WIDTH, GRAZE_HEIGHT)
  );
}

// One hit, in the order of the kill sequence: the damage, then a bullet with no pass-through
// left is spent while a bolt passing through remembers the body, then the reports.
function hitEnemy(bullet: Bullet, enemy: Enemy, rules: HitRules): void {
  const destroyed = applyDamage(enemy, bullet.damage);
  if (bullet.pierceLeft === 0) {
    bullet.spent = true;
  } else {
    bullet.recordHit(enemy.serial);
    bullet.pierceLeft -= 1;
  }
  rules.enemyHit(enemy);
  if (destroyed) rules.destroyEnemy(enemy);
}

// One hit on the boss: the same bullet bookkeeping, then `Run` takes over (ADR-0022 decision 5).
function hitBoss(bullet: Bullet, boss: Boss, rules: HitRules): void {
  if (bullet.pierceLeft === 0) {
    bullet.spent = true;
  } else {
    bullet.recordHit(boss.serial);
    bullet.pierceLeft -= 1;
  }
  rules.damageBoss(bullet.damage);
}

/**
 * The next body the bullet meets along its path: of the live enemies, and the boss while it is
 * targetable, that it overlaps and has not hit, the one nearest along its velocity (smallest
 * position · velocity), so a bolt crossing a stack hits the closest first, whatever the active
 * list's order. `undefined` when none is left.
 */
function nearestTarget(world: World, bullet: Bullet): Enemy | Boss | undefined {
  let nearest: Enemy | Boss | undefined;
  let nearestTravel = Number.POSITIVE_INFINITY;
  for (let j = 0; j < world.enemies.count; j += 1) {
    const enemy = world.enemies.at(j);
    if (enemy.spent || !overlaps(bullet, enemy) || !bullet.canHit(enemy.serial)) continue;
    const travel = enemy.position.x * bullet.velocity.x + enemy.position.y * bullet.velocity.y;
    if (travel < nearestTravel) {
      nearest = enemy;
      nearestTravel = travel;
    }
  }
  const boss = world.boss;
  if (boss.isTargetable() && overlaps(bullet, boss) && bullet.canHit(boss.serial)) {
    const travel = boss.position.x * bullet.velocity.x + boss.position.y * bullet.velocity.y;
    if (travel < nearestTravel) nearest = boss;
  }
  return nearest;
}

// Pass 1: player bullets against enemies and the fighting boss, nearest first along the bullet's
// path. A shot with no pass-through left is spent on its hit; one with a pass-through records the
// body and goes on (ADR-0019). Each hit spends the bullet, destroys the enemy or records it, so
// the loop ends.
function shotsAgainstEnemies(world: World, rules: HitRules): void {
  for (let i = 0; i < world.playerBullets.count; i += 1) {
    const bullet = world.playerBullets.at(i);
    while (!bullet.spent) {
      const target = nearestTarget(world, bullet);
      if (target === undefined) break;
      if (target instanceof Boss) hitBoss(bullet, target, rules);
      else hitEnemy(bullet, target, rules);
    }
  }
}

// Reports a hit that counted: a death, or the shield taking it.
function report(outcome: HitOutcome, rules: HitRules): void {
  if (outcome === HitOutcome.Died) rules.playerDied();
  else if (outcome === HitOutcome.Absorbed) rules.shieldBroken();
}

// Pass 2: enemy bullets against the hitbox, or the ring while a charge is held. A bullet that
// killed the ship or broke its shield is consumed; an ignored hit passes through. A bullet that
// misses the hitbox but crosses the drawn ship grazes, once, and stays live (GDD v0.31 §4.1).
function bulletsAgainstPlayer(world: World, rules: HitRules): void {
  for (let i = 0; i < world.enemyBullets.count; i += 1) {
    const bullet = world.enemyBullets.at(i);
    if (bullet.spent) continue;
    if (!touchesPlayer(world.player, bullet)) {
      if (grazes(world.player, bullet)) {
        bullet.grazed = true;
        rules.bulletGrazed(bullet);
      }
      continue;
    }
    const outcome = world.player.hit();
    if (outcome === HitOutcome.Ignored) continue;
    bullet.spent = true;
    report(outcome, rules);
  }
}

// Pass 3: enemy bodies, and the boss while it enters or fights, against the hitbox or the ring;
// contact hurts the player, never the enemy (GDD §5.2), and a body the shield absorbed survives.
function bodiesAgainstPlayer(world: World, rules: HitRules): void {
  for (let i = 0; i < world.enemies.count; i += 1) {
    const enemy = world.enemies.at(i);
    if (enemy.spent || !touchesPlayer(world.player, enemy)) continue;
    report(world.player.hit(), rules);
  }
  if (world.boss.isHarmful() && touchesPlayer(world.player, world.boss)) {
    report(world.player.hit(), rules);
  }
}

// The ship's full sprite box catches pickups, generously (GDD §4.6).
const COLLECT = PlayerTuning.collectSize;

function overlapsSprite(player: Player, box: Box): boolean {
  return inShipBox(player, box, COLLECT, COLLECT);
}

// Pass 4: pickups against the ship, after the hazards: a ship dying this step catches nothing.
// Several pickups in one step apply in the order of the active list (GDD v0.8 §4.6).
function pickupsAgainstPlayer(world: World, rules: HitRules): void {
  if (!world.player.canCollect) return;
  for (let i = 0; i < world.pickups.count; i += 1) {
    const pickup = world.pickups.at(i);
    if (pickup.spent || !overlapsSprite(world.player, pickup)) continue;
    pickup.spent = true;
    rules.pickupCollected(pickup);
  }
}

/**
 * Step 5 of `Run.step` (class diagram, `CollisionResolver`): marks only — spent bullets and
 * destroyed enemies are released at step 6, so no pool sees a double release.
 */
export const CollisionResolver = {
  resolve(world: World, rules: HitRules): void {
    shotsAgainstEnemies(world, rules);
    bulletsAgainstPlayer(world, rules);
    bodiesAgainstPlayer(world, rules);
    pickupsAgainstPlayer(world, rules);
  },
} as const;
